// Wire types mirroring internal/dashboard/*.go JSON tags. Keep field names
// and casing in exact sync. Never rename on the frontend — do it in a view
// layer if needed.

export interface User {
  id: string;
  email: string;
  account_id: string;
  role?: "owner" | "admin" | "member" | "viewer";
  emailVerified?: boolean;
  created_at: string;
}

export interface Environment {
  id: string;
  slug: string;
  account_id: string;
  created_at: string;
}

/** Commercial plan on the account (SaaS A.2/A.3). */
export type Plan = "free" | "pro" | "scale";

export interface App {
  id: string;
  name: string;
  sdk_url: string;
  /** Only present on create/rotate responses. GET returns just the last-4 hint. */
  signing_key_current?: string;
  /** last-4 characters of the current key, always present on list/get */
  signing_key_last4: string;
  last_synced_at?: string;
  created_at: string;
}

export type RunStatus = "pending" | "running" | "waiting" | "completed" | "failed" | "cancelled";

export interface RunSummary {
  id: string;
  function_id: string;
  function_slug: string;
  event_id?: string;
  event_name?: string;
  status: RunStatus;
  attempt: number;
  started_at: string;
  completed_at?: string;
}

export type StepType = "run" | "sleep" | "sleep_until" | "wait_for_event" | "send_event" | "call";

export interface StepHistoryItem {
  id: string;
  type: StepType;
  output?: unknown;
  event?: unknown;
  event_id?: string;
  started_at?: string;
  ended_at?: string;
}

/** A child run spawned by a step.call, linked to the parent's call step by call_id. */
export interface ChildRun {
  id: string;
  call_id?: string;
  function_slug: string;
  status: RunStatus;
}

export interface RunDetail {
  id: string;
  function_id: string;
  function_slug: string;
  event_id?: string;
  event_name?: string;
  status: RunStatus;
  attempt: number;
  step_history: StepHistoryItem[];
  child_runs?: ChildRun[];
  /** When a worker first picked the run up (execution start); null while queued. */
  claimed_at?: string;
  output?: unknown;
  error?: { name: string; message: string; stack?: string; retryable: boolean } | null;
  started_at: string;
  completed_at?: string;
}

export interface EventSummary {
  id: string;
  name: string;
  data: unknown;
  user?: unknown;
  received_at: string;
}

export interface Fn {
  id: string;
  slug: string;
  app_id: string;
  app_name: string;
  trigger_type: "event" | "cron";
  trigger_event?: string;
  trigger_cron?: string;
  /** Cron functions only; present in the detail response. */
  next_run_at?: string;
  /** Cron functions only; present in the detail response. Absent if never fired. */
  last_run_at?: string;
  config: unknown;
  archived_at?: string;
  created_at: string;
}

export interface Metrics {
  total: number;
  completed: number;
  failed: number;
  cancelled: number;
  running: number;
  pending: number;
  waiting: number;
  p50_ms: number;
  p95_ms: number;
  buckets: Array<{ hour_start: string; total: number; completed: number; failed: number }>;
}

export interface Schedule {
  function_id: string;
  function_slug: string;
  cron: string;
  next_run_at?: string;
  last_run_at?: string;
  runs_24h: number;
  failed_24h: number;
}

export interface EventKey {
  id: string;
  name: string;
  /** What this key may do: "events:write", and optionally "runs:read". */
  scopes: string[];
  last_used_at?: string;
  created_at: string;
}

export interface Paged<T> {
  data: T[];
  next_cursor?: string;
}

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly body?: unknown) {
    // Prefix message with status so QueryClient can inspect it cheaply.
    super(`${status} ${message}`);
    this.name = "ApiError";
  }
}
